import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The state glyphs (design-spec §4, variant E), drawn once and shared by `ArtifactStateMark`,
 * `Notice` and `ProblemBox`. Four shapes that stay distinct in greyscale (P5): a dotted ring,
 * a barred ring, a filled triangle and an info ring. Only the failed triangle is red; the
 * others take the ink of what they sit in.
 */
const stroke = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
};
/** Pending: a dotted ring, 16 (28 in the well's loading frame). */
export function PendingGlyph({ size = 16 } = {}) {
    return (_jsx("svg", { "data-glyph": "pending", width: size, height: size, viewBox: "0 0 24 24", "aria-hidden": "true", style: { flex: "none" }, children: _jsx("circle", { cx: 12, cy: 12, r: 8, strokeDasharray: "2.6 3.2", ...stroke }) }));
}
/** Absent: a ring with a bar through it, 16. */
export function AbsentGlyph() {
    return (_jsxs("svg", { "data-glyph": "absent", width: 16, height: 16, viewBox: "0 0 24 24", "aria-hidden": "true", style: { flex: "none" }, children: [_jsx("circle", { cx: 12, cy: 12, r: 8, ...stroke }), _jsx("path", { d: "M8 12h8", ...stroke })] }));
}
/**
 * Failed: a red filled triangle with a white mark, 18. Inside the dark well `--problem` is
 * 2.53:1, so there it takes `--problem-on-well` with a `--well` mark (design-spec §2.1).
 */
export function FailedGlyph({ size = 18, onWell = false } = {}) {
    const mark = onWell ? "var(--well)" : "var(--on-problem)";
    return (_jsxs("svg", { "data-glyph": "failed", width: size, height: size, viewBox: "0 0 24 24", "aria-hidden": "true", style: { flex: "none" }, children: [_jsx("path", { d: "M12 3 22 20H2z", style: { fill: onWell ? "var(--problem-on-well)" : "var(--problem)" } }), _jsx("path", { d: "M12 9.5v4.8", strokeWidth: 2, strokeLinecap: "round", style: { stroke: mark } }), _jsx("circle", { cx: 12, cy: 17.1, r: 1.15, style: { fill: mark } })] }));
}
/** Refused or partial: an info ring in the surrounding ink, 18. */
export function InfoGlyph() {
    return (_jsxs("svg", { "data-glyph": "info", width: 18, height: 18, viewBox: "0 0 24 24", "aria-hidden": "true", style: { flex: "none" }, children: [_jsx("circle", { cx: 12, cy: 12, r: 9, ...stroke }), _jsx("path", { d: "M12 11v5.5", ...stroke }), _jsx("circle", { cx: 12, cy: 7.8, r: 0.6, style: { fill: "currentColor" } })] }));
}
/** Expiry soon (10 days or fewer): a hollow hourglass in the surrounding ink, 15. */
export function HourglassGlyph() {
    return (_jsx("svg", { "data-glyph": "hourglass", width: 15, height: 15, viewBox: "0 0 24 24", "aria-hidden": "true", style: { flex: "none" }, children: _jsx("path", { d: "M7 3h10M7 21h10M8 3c0 5 8 5 8 9s-8 4-8 9M16 3c0 5-8 5-8 9s8 4 8 9", ...stroke }) }));
}
/** Expiry urgent (5 days or fewer): a red filled clock with white hands, 17. */
export function ClockGlyph() {
    return (_jsxs("svg", { "data-glyph": "clock", width: 17, height: 17, viewBox: "0 0 24 24", "aria-hidden": "true", style: { flex: "none" }, children: [_jsx("circle", { cx: 12, cy: 12, r: 10, style: { fill: "var(--problem)" } }), _jsx("path", { d: "M12 6.8V12l3.4 2.2", fill: "none", strokeWidth: 2, strokeLinecap: "round", style: { stroke: "var(--on-problem)" } })] }));
}
